import axios from "axios";
import store from "@/store"; // Import your Vuex store
import { auth, firebaseConfig } from "../plugins/firebaseconfig"; // Import the initialized auth instance and firebaseConfig

// Determines if the application is running in production mode
// const isProduction = import.meta.env.PROD;
const production = process.env.VITE_APP_ENVIROMENT
  ? process.env.VITE_APP_ENVIROMENT == "production"
  : false;

// Sets the backend endpoint based on the environment

const BACKEND_ENDPOINT = production
  ? process.env.VITE_APP_BACKEND_ENDPOINT
  : "/api";

/**
 * Creates an axios instance with predefined configuration
 * including baseURL, content type, and CORS settings.
 */
const axiosInstance = axios.create({
  baseURL: BACKEND_ENDPOINT,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
});

/**
 * Refreshes the Firebase ID token using the refresh token.
 * @returns {Promise<string>} - A promise that resolves to the new Firebase ID token.
 * @throws {Error} - Throws an error if the token refresh process fails.
 */
const refreshFirebaseToken = async () => {
  try {
    const firebaseToken = await auth.currentUser.getIdToken(true);

    // Store the new tokens in the Vuex store
    store.commit("setToken", firebaseToken);

    return firebaseToken;
  } catch (error) {
    console.error("Error refreshing Firebase token:", error);
    throw error;
  }
};

// Axios request interceptor to add authentication token and store ID
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = store.state.token;

    // Retrieve the storeId from Vuex store
    const storeId = store.state.storeId;

    const lang = store.state.currentLang;

    // Include the storeId in the request headers
    config.headers["storeId"] = storeId;
    config.headers["lang"] = lang;

    // Include the authentication token in the request headers
    if (token) {
      config.headers["X-ACCESS-TOKEN"] = token;
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Axios response interceptor for handling responses and errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error("Response error:", error);

    // If the error is due to an expired token
    if (
      error.response &&
      ((error.response.status === 401 &&
      error.response.statusText == "Unauthorized") || error.response.status === 500) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Refresh the token using Firebase
        const newToken = await refreshFirebaseToken();

        // Update the token in Vuex store and localStorage
        store.commit("setToken", newToken);

        // Update the token in the original request headers
        originalRequest.headers["X-ACCESS-TOKEN"] = newToken;

        // Retry the original request with the new token
        return axiosInstance(originalRequest);
      } catch (tokenRefreshError) {
        console.error("Token refresh failed:", tokenRefreshError);
        // Handle user logout if token refresh fails
        store.dispatch("userLoggedOut");
        return Promise.reject(tokenRefreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
